import React, { useState, useEffect, useRef, useCallback } from "react";
import PageHeader from "../components/layout/PageHeader";
import useSetPageTitle from "../hooks/useSetPageTitle";
import useClickOutside from "../hooks/useClickOutside";
import { formatDate } from "../utils/date";
import EmptyState from "../components/EmptyState";
import Modal from "../components/ui/Modal";
import { SearchInput } from "../components/ui/search-input";
import { Badge } from "../components/ui/badge";
import {
  Plus,
  MoreHorizontal,
  FlaskConical,
  Search,
  Pencil,
  Archive,
  Phone,
  Globe,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { useWsNavigate } from "../hooks/useWsNavigate";
import { aiTestingAgentAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import { useCan } from "../hooks/useCan";
import { usePostHog } from "@posthog/react";

const AITestingAgent = () => {
  useSetPageTitle("Test Agents");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [error, setError] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null); // agent pending archive confirmation
  const [archiving, setArchiving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { currentWorkspace } = useAuth();
  const { agents: contextAgents, agentsLoading } = useAgent();
  const navigate = useWsNavigate();
  const canWriteAgents = useCan("agent", "write");
  const posthog = usePostHog();
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Use agents from context — no duplicate API call
  useEffect(() => {
    setAgents(contextAgents || []);
  }, [contextAgents]);

  const loading = agentsLoading;

  const closeDropdown = useCallback(() => setActiveDropdown(null), []);
  useClickOutside(dropdownRef, closeDropdown, activeDropdown !== null);

  const handleAgentClick = (agentId) => {
    navigate(`/edit-agent/${agentId}`);
  };

  const handleDropdownClick = (agentId, e) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === agentId ? null : agentId);
  };

  const handleEdit = (agentId, e) => {
    e.stopPropagation();
    navigate(`/edit-agent/${agentId}`);
    setActiveDropdown(null);
  };

  // Open the archive confirmation dialog for an agent.
  const openArchiveDialog = (agent, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setArchiveTarget(agent);
  };

  // Perform the archive once the user confirms in the dialog.
  const confirmArchive = async () => {
    if (!archiveTarget || archiving) return;
    const agentId = archiveTarget.id;
    setArchiving(true);
    try {
      await aiTestingAgentAPI.deleteAgent(agentId, currentWorkspace.id);
      setAgents(agents.filter((agent) => agent.id !== agentId));
      posthog?.capture('agent_archived', { agent_id: agentId });
      setArchiveTarget(null);
    } catch (err) {
      console.error("Error archiving agent:", err);
      setError("Failed to archive agent");
    } finally {
      setArchiving(false);
    }
  };

  const getSourceLabel = (agent) => {
    if (agent.integration?.vendor) {
      return agent.integration.vendor.charAt(0).toUpperCase() + agent.integration.vendor.slice(1);
    }
    if (agent.integration_type?.startsWith("external_")) {
      return agent.integration_type.replace("external_", "").charAt(0).toUpperCase() +
        agent.integration_type.replace("external_", "").slice(1);
    }
    return "Self-managed";
  };

  const getSourceIcon = (agent) => {
    if (agent.integration?.vendor || agent.integration_type?.startsWith("external_")) {
      return <LinkIcon className="h-3.5 w-3.5" />;
    }
    return <FlaskConical className="h-3.5 w-3.5" />;
  };

  const filteredAgents = agents.filter((agent) =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAgents = filteredAgents.slice(startIndex, startIndex + itemsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="px-4">
      <PageHeader description="Each test agent is configured to call and evaluate one of your AI agents.">
        <SearchInput
          className="w-60"
          placeholder="Search agents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {canWriteAgents && (
          <button
            onClick={() => navigate("/ai-testing-agent/create")}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Agent
          </button>
        )}
      </PageHeader>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      ) : agents.length === 0 ? (
        <EmptyState
          compact
          icon={FlaskConical}
          title="No Test Agents Yet"
          description="Create your first AI testing agent to start automating your testing processes."
          primaryAction={
            canWriteAgents
              ? {
                  label: "Create Agent",
                  onClick: () => navigate("/ai-testing-agent/create"),
                }
              : undefined
          }
        />
      ) : (
        <div>
          {/* Agent list */}
          <div className="space-y-3">
            {paginatedAgents.map((agent) => {
              const initials = agent.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .substring(0, 2)
                .toUpperCase();

              return (
                <div
                  key={agent.id}
                  className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3 cursor-pointer hover:border-input transition-all group"
                  onClick={() => handleAgentClick(agent.id)}
                >
                  {/* Left: avatar + info */}
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-sm font-semibold">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-foreground truncate">
                          {agent.name}
                        </h3>
                        <Badge variant="muted">
                          {getSourceIcon(agent)}
                          {getSourceLabel(agent)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="font-mono text-xs text-muted-foreground">ID {agent.id}</span>
                        {agent.phone_number && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" />
                            {agent.phone_number}
                          </span>
                        )}
                        {agent.conversation_type && (
                          <span className="flex items-center gap-1 capitalize">
                            <Globe className="h-3.5 w-3.5" />
                            {agent.conversation_type}
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          Created {formatDate(agent.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: actions */}
                  {canWriteAgents && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="Edit"
                        onClick={(e) => handleEdit(agent.id, e)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <div className="relative" ref={activeDropdown === agent.id ? dropdownRef : null}>
                        <button
                          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          onClick={(e) => handleDropdownClick(agent.id, e)}
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </button>
                        {activeDropdown === agent.id && (
                          <div className="absolute right-0 mt-1 w-40 bg-card rounded-lg shadow-lg z-10 border border-border py-1">
                            <button
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                              onClick={(e) => handleEdit(agent.id, e)}
                            >
                              <Pencil className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              onClick={(e) => openArchiveDialog(agent, e)}
                            >
                              <Archive className="h-4 w-4" />
                              Archive
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredAgents.length)} of {filteredAgents.length} agents
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg text-sm ${
                    currentPage === 1
                      ? "text-muted-foreground/50 cursor-not-allowed"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium ${
                      currentPage === page
                        ? "bg-black text-white"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg text-sm ${
                    currentPage === totalPages
                      ? "text-muted-foreground/50 cursor-not-allowed"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* No search results */}
          {filteredAgents.length === 0 && searchTerm && (
            <div className="flex flex-col items-center justify-center py-16">
              <Search className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">No agents match "{searchTerm}"</p>
              <button
                className="mt-2 text-sm text-primary hover:underline"
                onClick={() => setSearchTerm("")}
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      )}
      {archiveTarget && (
        <Modal
          title="Archive this agent?"
          onClose={() => { if (!archiving) setArchiveTarget(null); }}
          maxWidth="max-w-md"
          footer={
            <>
              <button
                onClick={() => setArchiveTarget(null)}
                disabled={archiving}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmArchive}
                disabled={archiving}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {archiving ? "Archiving…" : "Archive agent"}
              </button>
            </>
          }
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-amber-50">
              <Archive className="h-4 w-4 text-amber-600" />
            </div>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Archiving <span className="font-semibold">{archiveTarget.name}</span> will hide it
                from your active agents, along with everything tied to it:
              </p>
              <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
                <li>Its <span className="font-medium">conversation calls</span> are hidden from Observability</li>
                <li>Its <span className="font-medium">batch runs</span> and run history</li>
                <li><span className="font-medium">Scenarios used only by this agent</span> are archived too</li>
              </ul>
              <p className="text-sm text-gray-600">
                Scenarios and metrics <span className="font-medium">shared with other agents stay intact</span>,
                and nothing is permanently deleted — you can restore this agent anytime.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AITestingAgent;
